/**
 * Whether a provider is outside the account's plan, and so must not be
 * offered as connectable (AB#22375).
 *
 * `plans.features["allowed_integrations"]` had been advertised on the Billing
 * plan cards since they shipped and read by nothing, so a Starter account
 * could connect Salesforce, HubSpot, Twilio and the rest and the Integrations
 * tab would report them connected.
 *
 * Extracted as a plain function rather than living inside the component so the
 * decision is testable without rendering the whole Integrations panel — and so
 * it can be read next to the server-side gate it must agree with
 * (`plan_gating.integration_fields_outside_plan`).
 *
 * UX only. `PUT /auth/config` re-checks every one of these rules; this exists
 * so the UI does not offer a control that can only 403.
 */
export function isIntegrationLockedByPlan(
  /** From GET /auth/plan-status. `null`/`undefined` = the plan carries no list. */
  allowedIntegrations: string[] | null | undefined,
  /** The provider's catalog id, e.g. 'salesforce'. */
  providerId: string,
  /** Whether credentials for it are already stored on the account. */
  alreadyConnected: boolean,
): boolean {
  // No list means "unknown", not "nothing allowed". The server treats a plan
  // with no list as imposing no restriction; guessing the other way here would
  // lock every integration in the product off the back of a seeding gap.
  if (!allowedIntegrations) return false

  if (allowedIntegrations.includes(providerId)) return false

  // Grandfathered. The bug shipped, so accounts hold out-of-plan credentials
  // today; the server still accepts an update to one so a working key stays
  // rotatable, and showing it as locked would misdescribe a live connection.
  return !alreadyConnected
}
